import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Archive, Trash, Files, MagnifyingGlass, ArrowCounterClockwise, Copy, Upload } from "@phosphor-icons/react";
import { formatMoney, timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ImportCompaniesDialog from "@/components/admin/ImportCompaniesDialog";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();

  const load = () => {
    api.get("/companies", { params: { include_archived: includeArchived, q: q || undefined, sort: "recently_added" } }).then((r) => setCompanies(r.data));
  };

  useEffect(() => { load(); }, [q, includeArchived]);

  const archive = async (id, archived) => {
    await api.post(`/companies/${id}/archive?archived=${archived}`);
    toast.success(archived ? "Archived" : "Unarchived");
    load();
  };
  const remove = async (id) => {
    await api.delete(`/companies/${id}`);
    toast.success("Deleted");
    load();
  };
  const duplicate = async (id) => {
    try {
      const r = await api.post(`/companies/${id}/duplicate`);
      toast.success(`Duplicated as "${r.data.name}" (unpublished draft)`);
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Admin · Companies</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Company management</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-companies-btn">
            <Upload size={16} className="mr-1.5" /> Import from Excel
          </Button>
          <Button onClick={() => nav("/admin/companies/new")} data-testid="new-company-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            <Plus size={16} className="mr-1.5" /> New company
          </Button>
        </div>
      </div>

      <ImportCompaniesDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9" data-testid="admin-search-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="accent-emerald-600" />
          Include archived
        </label>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead>Company</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead className="text-right">Raise</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Link to={`/companies/${c.id}`} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded bg-secondary overflow-hidden">
                      {c.logo_url && <img src={c.logo_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <span className="font-medium">{c.name}</span>
                  </Link>
                </TableCell>
                <TableCell className="text-sm">{c.industry}</TableCell>
                <TableCell className="text-sm">{c.stage}</TableCell>
                <TableCell className="text-right num-display">{formatMoney(c.fundraising?.amount_raising)}</TableCell>
                <TableCell>
                  {c.archived ? <span className="text-xs uppercase tracking-wider text-muted-foreground">Archived</span> :
                   c.published ? <span className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Live</span> :
                   <span className="text-xs uppercase tracking-wider text-amber-600">Draft</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{timeAgo(c.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Link to={`/admin/companies/${c.id}/documents`}>
                      <Button variant="ghost" size="icon" title="Documents" data-testid={`docs-btn-${c.id}`}><Files size={16} /></Button>
                    </Link>
                    <Link to={`/admin/companies/${c.id}/edit`}>
                      <Button variant="ghost" size="icon" title="Edit" data-testid={`edit-btn-${c.id}`}><Pencil size={16} /></Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => duplicate(c.id)} title="Duplicate" data-testid={`duplicate-btn-${c.id}`}>
                      <Copy size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => archive(c.id, !c.archived)} title={c.archived ? "Unarchive" : "Archive"} data-testid={`archive-btn-${c.id}`}>
                      {c.archived ? <ArrowCounterClockwise size={16} /> : <Archive size={16} />}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30" title="Delete" data-testid={`delete-btn-${c.id}`}>
                          <Trash size={16} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                          <AlertDialogDescription>This will permanently delete the company and all its documents. Consider archiving instead.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
